import React, { forwardRef } from 'react';

import { cn } from '../../lib/css/className/className';
import { useStack } from '../../hooks/useStack';

import { StackProps, HVStackProps } from './types';

const cls = cn('Stack');

/**
 * Компонент-контейнер для позиционирования дочерних компонентов.
 * Используется для создания кнопок, полей ввода, форм, списков и т.д.
 */
export const Stack = function Stack({
    className,
    children,

    type = 'horizontal',
    scrollAxis,

    flexWrap,
    justifyContent,
    alignItems,

    gap,

    as: ElementTag = 'div',
    inline = false,
    ...props
}: StackProps) {
    const stackClasses = useStack({
        type,
        scrollAxis,
        gap,
        flexWrap,
        justifyContent,
        alignItems,
        inline,
    });

    return (
        <ElementTag className={cls({}, [stackClasses, className])} data-testid={props['data-testid']} {...props}>
            {children}
        </ElementTag>
    );
};

/**
 * Компонент-контейнер для горизонтального позиционирования дочерних компонентов (в строку).
 * Используется для создания кнопок, полей ввода и т.д.
 */
export const HStack = forwardRef(function HStack(
    { scrollable, ...otherProps }: HVStackProps,
    ref: React.ForwardedRef<HTMLElement>,
) {
    const scrollAxis = scrollable ? 'x' : undefined;
    return Stack({ scrollAxis: scrollAxis, ref, ...otherProps });
});

HStack.displayName = 'HStack';

/**
 * Компонент-контейнер для вертикального позиционирования дочерних компонентов (в столбец).
 * Используется для создания форм, списков и т.д.
 */
export const VStack = forwardRef(function VStack(
    { scrollable, ...otherProps }: HVStackProps,
    ref: React.ForwardedRef<HTMLElement>,
) {
    const scrollAxis = scrollable ? 'y' : undefined;
    return Stack({ type: 'vertical', scrollAxis: scrollAxis, ref, ...otherProps });
});

VStack.displayName = 'VStack';
